/**
 * CalendarView.jsx — Calendar with Month/Week/Agenda/Year views
 *
 * Features:
 * - Month view: day grid with job bars, click-to-select, double-click drill-down to week
 * - Week view: 7-day columns with jobs, deliveries, legals, crew names
 * - Agenda view: 30-day chronological list
 * - Year view: 12-month mini-calendar overview
 * - Navigate between months/weeks/years
 * - Click on a day to see jobs + "Create Job" button
 * - Today highlighted
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useJobs } from '../../hooks/useJobs';
import { useCalendarEvents, useCreateCalendarEvent, useDeleteCalendarEvent } from '../../hooks/useCalendarEvents';
import { useLegalDocs } from '../../hooks/useLegal';
import { useAuth } from '../../hooks/useAuth';
import { useRoster } from '../../hooks/useRoster';
import { navigateToJob } from '../../lib/crossNav';
import { normalizeSnapshot, deptLeads } from '../../lib/calcEngine';
import { truncate, STATUS_COLORS, fmtD, uid } from '../../lib/utils';
import useWeatherForecast, { weatherInfo, matchCity } from '../../hooks/useWeatherForecast';
import FocusHide from '../shared/FocusHide';

// ── Calendar event type colors ──────────────────────────────────────────────
const CAL_COLORS = {
  shoot:    '#2E8B57', // green
  delivery: '#C94A2A', // rust
  legal:    '#7B5EA7', // purple
  custom:   '#8B5CF6', // violet
};

// ── Hold status from job status ─────────────────────────────────────────────
function getHoldStatus(jobStatus) {
  if (!jobStatus) return '';
  const s = jobStatus.toLowerCase();
  if (s === 'intake' || s === 'quoted') return 'soft';
  if (['confirmed','shooting','post_production','partial_delivery','delivered','invoiced','paid'].includes(s)) return 'conf';
  return '';
}

// ── Extract shoot time from calcSnapshot.deliverableLines ──────────────
function getShootTime(job) {
  if (!job.calcSnapshot) return null;
  const raw = typeof job.calcSnapshot === 'string' ? JSON.parse(job.calcSnapshot) : job.calcSnapshot;
  const lines = raw.deliverableLines || [];
  const times = lines.map(l => l.shootTime).filter(Boolean).sort();
  if (!times.length) return null;
  return times[0]; // earliest time
}

// Format 24h time to compact display: "11:00" → "11a", "14:30" → "2:30p"
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'p' : 'a';
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m ? `${hr}:${String(m).padStart(2, '0')}${suffix}` : `${hr}${suffix}`;
}

// ── Extract crew names from calcSnapshot ────────────────────────────────
function getCrewNames(job) {
  if (!job.calcSnapshot) return [];
  const raw = typeof job.calcSnapshot === 'string' ? JSON.parse(job.calcSnapshot) : job.calcSnapshot;
  const snap = normalizeSnapshot(raw);
  const names = new Set();
  (snap.originators || []).forEach(o => { if (o.name) names.add(o.name); });
  deptLeads(snap.prod).forEach(l => { if (l?.name) names.add(l.name); });
  (snap.prod?.hourly || []).forEach(h => { if (h.name) names.add(h.name); });
  deptLeads(snap.post).forEach(l => { if (l?.name) names.add(l.name); });
  (snap.post?.hourly || []).forEach(h => { if (h.name) names.add(h.name); });
  return [...names];
}

export default function CalendarView({ onCreateJob, calendarScope = 'mine', defaultView = 'month' }) {
  const { data: rawJobs = [] } = useJobs();
  const { data: calEvents = [] } = useCalendarEvents();
  const { data: legalDocs = [] } = useLegalDocs();
  const { data: roster = [] } = useRoster();
  const { user } = useAuth();
  const createCalEvent = useCreateCalendarEvent();
  const deleteCalEvent = useDeleteCalendarEvent();
  const { forecast } = useWeatherForecast();

  // Resolve logged-in user's roster ID
  const myRosterId = useMemo(() => {
    if (!user?.email || !roster.length) return null;
    const match = roster.find(r => r.email === user.email);
    return match?.id || null;
  }, [user?.email, roster]);

  // Filter jobs by scope
  const allJobs = useMemo(() => {
    if (calendarScope !== 'mine' || !myRosterId) return rawJobs;
    return rawJobs.filter(job => {
      const snap = job.calcSnapshot
        ? (typeof job.calcSnapshot === 'string' ? JSON.parse(job.calcSnapshot) : job.calcSnapshot)
        : null;
      if (!snap) return false;
      const ids = new Set();
      (snap.originators || []).forEach(o => { if (o.rosterId || o.roster_id) ids.add(o.rosterId || o.roster_id); });
      deptLeads(snap.prod).forEach(pl => { if (pl?.rosterId || pl?.roster_id) ids.add(pl.rosterId || pl.roster_id); });
      (snap.prod?.hourly || []).forEach(h => { if (h.rosterId || h.roster_id) ids.add(h.rosterId || h.roster_id); });
      deptLeads(snap.post).forEach(ptl => { if (ptl?.rosterId || ptl?.roster_id) ids.add(ptl.rosterId || ptl.roster_id); });
      (snap.post?.hourly || []).forEach(h => { if (h.rosterId || h.roster_id) ids.add(h.rosterId || h.roster_id); });
      return ids.has(myRosterId);
    });
  }, [rawJobs, calendarScope, myRosterId]);
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewMode, setViewMode] = useState(defaultView); // 'month' | 'week' | 'year'
  const [showEventModal, setShowEventModal] = useState(false);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', notes: '', jobId: '' });
  const [newJobDate, setNewJobDate] = useState('');
  const [expandedDay, setExpandedDay] = useState(null); // dateStr for overflow popover
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('action') === 'addEvent') {
      const d = new Date(); const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      setEventForm({ title: '', date: ds, time: '', notes: '', jobId: '' });
      setShowEventModal(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Get year and month
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  // Map jobs to dates
  const jobsByDate = useMemo(() => {
    const map = {};
    allJobs.forEach((job) => {
      if (!job.shootDate) return;
      const dateStr = job.shootDate.split('T')[0];
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(job);

      if (job.shootDates && Array.isArray(job.shootDates)) {
        job.shootDates.forEach((d) => {
          const ds = d.split('T')[0];
          if (ds !== dateStr) {
            if (!map[ds]) map[ds] = [];
            map[ds].push(job);
          }
        });
      }
    });
    return map;
  }, [allJobs]);

  // Custom events by date (C4)
  const eventsByDate = useMemo(() => {
    const map = {};
    calEvents.forEach((ev) => {
      if (!ev.date) return;
      const dateStr = ev.date.split('T')[0];
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(ev);
    });
    return map;
  }, [calEvents]);

  // Delivery events by date — one event per deliverable line
  // "delivered" flag uses per-deliverable deliveryStatus tracking when available,
  // falls back to job status only when no per-line tracking exists.
  const deliveriesByDate = useMemo(() => {
    const map = {};
    const activeStatuses = ['intake','quoted','confirmed','shooting','post_production','partial_delivery','delivered','invoiced'];
    allJobs.forEach((job) => {
      if (!activeStatuses.includes(job.status)) return;
      const dsArr = Array.isArray(job.deliveryStatus) ? job.deliveryStatus : [];
      // Prefer per-line entries from calcSnapshot
      if (job.calcSnapshot) {
        const snap = typeof job.calcSnapshot === 'string' ? JSON.parse(job.calcSnapshot) : job.calcSnapshot;
        const lines = (snap.deliverableLines || []).filter(l => l.deliveryDate);
        if (lines.length > 0) {
          lines.forEach((line, idx) => {
            const lds = line.deliveryDate.split('T')[0];
            if (!map[lds]) map[lds] = [];
            // Check per-deliverable sent status from deliveryStatus tracking
            const lineSent = dsArr[idx]?.sent === true;
            map[lds].push({
              type: 'delivery',
              title: `${job.title} — ${line.desc || 'Deliverable'}`,
              sub: job.clientName || '',
              jobId: job.id,
              status: job.status,
              delivered: lineSent,
            });
          });
          return; // skip project-level fallback
        }
      }
      // Fallback: project-level delivery date only (no deliverable lines)
      if (job.deliveryDate) {
        const ds = job.deliveryDate.split('T')[0];
        if (!map[ds]) map[ds] = [];
        // No per-line tracking — fall back to job status
        const allSent = dsArr.length > 0 && dsArr.every(d => d.sent);
        map[ds].push({
          type: 'delivery',
          title: job.title || 'Delivery',
          sub: job.clientName || '',
          jobId: job.id,
          status: job.status,
          delivered: allSent,
        });
      }
    });
    return map;
  }, [allJobs]);

  // Legal events by date — pending legal docs with dates
  const legalsByDate = useMemo(() => {
    const map = {};
    legalDocs.forEach((doc) => {
      if (!doc.date || doc.status === 'void') return;
      const ds = doc.date.split('T')[0];
      if (!map[ds]) map[ds] = [];
      map[ds].push({
        type: 'legal',
        title: `📑 ${doc.type || 'Legal'} — ${doc.party || ''}`,
        status: doc.status,
        docId: doc.id,
      });
    });
    return map;
  }, [legalDocs]);

  // ── Navigation ──────────────────────────────────────────────────────────
  const handlePrev = () => {
    if (viewMode === 'year') {
      setCurrentDate(new Date(year - 1, month, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(new Date(year, month - 1, 1));
    } else {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 7);
      setCurrentDate(d);
    }
    setSelectedDay(null);
  };

  const handleNext = () => {
    if (viewMode === 'year') {
      setCurrentDate(new Date(year + 1, month, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(new Date(year, month + 1, 1));
    } else {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 7);
      setCurrentDate(d);
    }
    setSelectedDay(null);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  // ── Date helpers ───────────────────────────────────────────────────────
  const toDateStr = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${dy}`;
  };

  const isSameDay = (d1, d2) => toDateStr(d1) === toDateStr(d2);

  // ── Month view grid (35 cells = 5 weeks, includes adjacent months) ──
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate(); // last day of prev month
  const monthCells = useMemo(() => {
    const cells = [];
    // Previous month trailing days
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const m = month === 0 ? 12 : month;
      const y = month === 0 ? year - 1 : year;
      cells.push({ day: d, month: m - 1, year: y, current: false });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, month, year, current: true });
    }
    // Next month leading days to fill 35 (5 rows)
    const remaining = 35 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const m = month === 11 ? 0 : month + 1;
      const y = month === 11 ? year + 1 : year;
      cells.push({ day: d, month: m, year: y, current: false });
    }
    return cells;
  }, [year, month, firstDay, daysInMonth, prevMonthDays]);

  const monthName = new Date(year, month).toLocaleDateString('en-US', { month: 'long' });

  // ── Week view days ──────────────────────────────────────────────────
  const weekStart = useMemo(() => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - d.getDay()); // Sunday
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentDate]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const weekLabel = useMemo(() => {
    const s = weekDays[0];
    const e = weekDays[6];
    const opts = { month: 'short', day: 'numeric' };
    return `${s.toLocaleDateString('en-US', opts)} — ${e.toLocaleDateString('en-US', opts)}, ${e.getFullYear()}`;
  }, [weekDays]);

  // ── Click-to-create ──────────────────────────────────────────────────
  const handleCreateJob = (dateStr) => {
    if (onCreateJob) {
      onCreateJob(dateStr);
    } else {
      navigate(`/jobs?action=newJob&shootDate=${dateStr}`);
    }
  };

  // ── Contextual agenda renderer ────────────────────────────────────────
  const renderAgenda = (startStr, endStr, title) => {
    const allItems = [];
    allJobs
      .filter((j) => j.shootDate && j.shootDate.split('T')[0] >= startStr && j.shootDate.split('T')[0] <= endStr)
      .forEach((job) => {
        allItems.push({ date: job.shootDate.split('T')[0], type: 'shoot', job });
      });
    Object.entries(deliveriesByDate).forEach(([ds, dels]) => {
      if (ds >= startStr && ds <= endStr) {
        dels.forEach((d) => allItems.push({ date: ds, type: 'delivery', ...d }));
      }
    });
    Object.entries(legalsByDate).forEach(([ds, legs]) => {
      if (ds >= startStr && ds <= endStr) {
        legs.forEach((l) => allItems.push({ date: ds, type: 'legal', ...l }));
      }
    });
    Object.entries(eventsByDate).forEach(([ds, evts]) => {
      if (ds >= startStr && ds <= endStr) {
        evts.forEach((ev) => allItems.push({ date: ds, type: 'custom', ev }));
      }
    });

    allItems.sort((a, b) => a.date.localeCompare(b.date));

    const grouped = {};
    allItems.forEach((item) => {
      if (!grouped[item.date]) grouped[item.date] = [];
      grouped[item.date].push(item);
    });

    const totalCount = allItems.length;

    return (
      <div className="card space-y-1">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-heading text-text-primary">{title}</h3>
          <span className="text-2xs text-text-dim">{totalCount} event{totalCount !== 1 ? 's' : ''}</span>
        </div>
        {Object.keys(grouped).length === 0 ? (
          <p className="text-text-dim text-sm py-4 text-center">No events in this period.</p>
        ) : (
          Object.entries(grouped).map(([dateStr, items]) => (
            <div key={dateStr} className="mb-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-xs font-semibold ${dateStr === toDateStr(today) ? 'text-accent' : 'text-text-primary'}`}>
                  {new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric',
                  })}
                </span>
                {dateStr === toDateStr(today) && (
                  <span className="text-2xs px-1.5 py-0.5 bg-accent/20 text-accent rounded font-semibold">Today</span>
                )}
              </div>
              <div className="space-y-1 pl-2 border-l-2 border-border ml-2">
                {items.map((item, idx) => {
                  if (item.type === 'shoot') {
                    const job = item.job;
                    const hold = getHoldStatus(job.status);
                    const city = matchCity(job.location);
                    const fw = forecast[city]?.[item.date];
                    const wi = fw ? weatherInfo(fw.code) : null;
                    return (
                      <div
                        key={`s-${job.id}`}
                        onClick={() => navigateToJob(navigate, job.id)}
                        className={`flex items-center gap-3 px-3 py-2 bg-surface-2 rounded cursor-pointer hover:border-accent/40 border border-border/30 transition-colors ${hold === 'soft' ? 'opacity-70 border-dashed' : ''}`}
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CAL_COLORS.shoot }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-text-primary truncate">
                            {(() => { const st = getShootTime(job); return st ? <span className="text-text-dim font-normal mr-1.5">{fmtTime(st)}</span> : null; })()}
                            {job.title}
                          </div>
                          <div className="text-2xs text-text-dim">
                            {job.clientName || 'N/A'}{job.location ? ` · ${job.location}` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {wi && (
                            <span className="text-2xs flex items-center gap-0.5 opacity-70" title={`${city}: ${wi.desc} · ${fw.high}°/${fw.low}° · ${fw.wind} mph`}>
                              <span style={{ fontSize: '12px' }}>{wi.icon}</span>
                              <span className="text-text-dim">{fw.high}°</span>
                            </span>
                          )}
                          {hold && (
                            <span className={`text-2xs px-1.5 py-0.5 rounded font-semibold ${hold === 'soft' ? 'bg-orange-400/20 text-orange-400' : 'bg-green/20 text-green'}`}>
                              {hold.toUpperCase()}
                            </span>
                          )}
                          <span className="text-2xs px-2 py-0.5 rounded bg-surface text-text-dim">{job.status}</span>
                        </div>
                      </div>
                    );
                  }
                  if (item.type === 'delivery') {
                    return (
                      <div
                        key={`d-${idx}`}
                        onClick={() => item.jobId && navigateToJob(navigate, item.jobId)}
                        className={`flex items-center gap-3 px-3 py-2 rounded cursor-pointer border transition-colors ${item.delivered ? 'opacity-50' : ''}`}
                        style={{ background: 'rgba(201,74,42,0.08)', borderColor: 'rgba(201,74,42,0.25)' }}
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CAL_COLORS.delivery }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold truncate" style={{ color: CAL_COLORS.delivery }}>
                            {item.delivered ? '✅' : '📦'} {item.title}
                          </div>
                          {item.sub && <div className="text-2xs text-text-dim">{item.sub}</div>}
                        </div>
                      </div>
                    );
                  }
                  if (item.type === 'legal') {
                    return (
                      <div
                        key={`l-${idx}`}
                        className="flex items-center gap-3 px-3 py-2 rounded border"
                        style={{ background: 'rgba(123,94,167,0.08)', borderColor: 'rgba(123,94,167,0.25)' }}
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CAL_COLORS.legal }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold truncate" style={{ color: CAL_COLORS.legal }}>{item.title}</div>
                        </div>
                        <span className="text-2xs px-2 py-0.5 rounded bg-surface text-text-dim">{item.status}</span>
                      </div>
                    );
                  }
                  if (item.type === 'custom') {
                    const ev = item.ev;
                    return (
                      <div
                        key={`c-${ev.id}`}
                        className="flex items-center gap-3 px-3 py-2 bg-surface-2 rounded border border-purple-400/30"
                      >
                        <div className="w-2.5 h-2.5 rounded-full bg-purple-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-text-primary truncate">{ev.title}</div>
                          {ev.time && <span className="text-2xs text-text-dim">{ev.time}</span>}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Calendar Stats Bar — multi-type counts + hold badges */}
      {(() => {
        const todayStr = toDateStr(today);
        const weekStr = toDateStr(new Date(Date.now() + 7 * 86400000));
        const monthStr = toDateStr(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        // Shoot counts
        const todayCount = (jobsByDate[todayStr] || []).filter(j => j.status !== 'void' && j.status !== 'archived').length;
        const weekCount = allJobs.filter(j => j.shootDate && j.shootDate >= todayStr && j.shootDate <= weekStr && j.status !== 'void' && j.status !== 'archived').length;
        const monthCount = allJobs.filter(j => j.shootDate && j.shootDate >= todayStr && j.shootDate <= monthStr && j.status !== 'void' && j.status !== 'archived').length;
        // Delivery counts (this month)
        const deliveryCount = Object.keys(deliveriesByDate).filter(k => k >= todayStr && k <= monthStr)
          .reduce((s, k) => s + deliveriesByDate[k].length, 0);
        // Legal counts
        const legalCount = Object.keys(legalsByDate).filter(k => k >= todayStr && k <= monthStr)
          .reduce((s, k) => s + legalsByDate[k].length, 0);
        // Custom event count
        const customCount = Object.keys(eventsByDate).filter(k => k >= todayStr && k <= monthStr)
          .reduce((s, k) => s + eventsByDate[k].length, 0);
        // Hold status counts (this month shoots)
        const monthShoots = allJobs.filter(j => j.shootDate && j.shootDate >= todayStr && j.shootDate <= monthStr);
        const softCount = monthShoots.filter(j => getHoldStatus(j.status) === 'soft').length;
        const confCount = monthShoots.filter(j => getHoldStatus(j.status) === 'conf').length;
        return (
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-border rounded">
              <span className="text-xs text-text-dim">Today:</span>
              <span className="text-sm font-heading text-accent">{todayCount}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-border rounded">
              <span className="text-xs text-text-dim">Week:</span>
              <span className="text-sm font-heading text-text-primary">{weekCount}</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-border rounded">
              <span className="w-2 h-2 rounded-full" style={{ background: CAL_COLORS.shoot }} />
              <span className="text-xs text-text-dim">{monthCount}</span>
              {deliveryCount > 0 && <>
                <span className="w-2 h-2 rounded-full" style={{ background: CAL_COLORS.delivery }} />
                <span className="text-xs text-text-dim">{deliveryCount}</span>
              </>}
              {legalCount > 0 && <>
                <span className="w-2 h-2 rounded-full" style={{ background: CAL_COLORS.legal }} />
                <span className="text-xs text-text-dim">{legalCount}</span>
              </>}
              {customCount > 0 && <>
                <span className="w-2 h-2 rounded-full" style={{ background: CAL_COLORS.custom }} />
                <span className="text-xs text-text-dim">{customCount}</span>
              </>}
            </div>
            {(softCount > 0 || confCount > 0) && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-border rounded">
                {softCount > 0 && (
                  <span className="text-2xs px-1.5 py-0.5 rounded font-semibold bg-orange-400/20 text-orange-400 border border-orange-400/30">
                    {softCount} SOFT
                  </span>
                )}
                {confCount > 0 && (
                  <span className="text-2xs px-1.5 py-0.5 rounded font-semibold bg-green/20 text-green border border-green/30">
                    {confCount} CONF
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Header with navigation + view toggle + export */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            className="px-3 py-1.5 bg-surface-2 hover:bg-surface-3 text-text-primary text-sm rounded transition-colors"
          >
            ←
          </button>
          <button
            onClick={handleToday}
            className="px-3 py-1.5 bg-surface border border-border hover:bg-surface-2 text-text-primary text-2xs rounded transition-colors"
          >
            Today
          </button>
          <button
            onClick={handleNext}
            className="px-3 py-1.5 bg-surface-2 hover:bg-surface-3 text-text-primary text-sm rounded transition-colors"
          >
            →
          </button>
        </div>

        <h2 className="text-lg font-heading text-text-primary">
          {viewMode === 'year' ? String(year) : viewMode === 'month' ? `${monthName} ${year}` : weekLabel}
        </h2>

        <div className="flex items-center gap-2">
          <FocusHide k="calendar.addEvent">
            <button
              onClick={() => {
                setEventForm({ title: '', date: toDateStr(today), time: '', notes: '' });
                setShowEventModal(true);
              }}
              className="px-3 py-1.5 bg-surface-2 hover:bg-surface-3 text-text-primary text-2xs font-semibold rounded transition-colors border border-border"
            >
              + Event
            </button>
          </FocusHide>
          <button
            onClick={() => {
              setNewJobDate(toDateStr(today));
              setShowNewJobModal(true);
            }}
            className="px-3 py-1.5 bg-accent text-bg text-2xs font-semibold rounded hover:bg-accent-hover transition-colors"
          >
            + New Job
          </button>
          {/* View toggle */}
          <div className="flex gap-1 bg-surface border border-border rounded p-0.5">
            {['week', 'month', 'year'].map((mode) => (
              <button
                key={mode}
                onClick={() => { setViewMode(mode); setSelectedDay(null); }}
                className={`px-2.5 py-1 text-2xs font-semibold rounded transition-colors ${
                  viewMode === mode
                    ? 'bg-accent text-bg'
                    : 'text-text-dim hover:text-text-primary'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── MONTH VIEW ─────────────────────────────────────────────── */}
      {viewMode === 'month' && (
        <div className="card p-2">
          {/* Day names */}
          <div className="grid grid-cols-7">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="text-center text-2xs font-semibold text-text-dim py-1.5 border-b border-border">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {monthCells.map((cell, idx) => {
              const dateStr = `${cell.year}-${String(cell.month + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`;
              const dayJobs = jobsByDate[dateStr] || [];
              const dayEvts = eventsByDate[dateStr] || [];
              const dayDeliveries = deliveriesByDate[dateStr] || [];
              const dayLegals = legalsByDate[dateStr] || [];
              const isToday = cell.current && isCurrentMonth && cell.day === today.getDate();
              const isSelected = cell.current && selectedDay === cell.day;
              const isWeekend = idx % 7 === 0 || idx % 7 === 6;

              // Build all visible items for this cell
              const cellItems = [];
              dayJobs.forEach((job) => cellItems.push({ kind: 'shoot', job }));
              dayDeliveries.forEach((del) => cellItems.push({ kind: 'delivery', del }));
              dayLegals.forEach((leg) => cellItems.push({ kind: 'legal', leg }));
              dayEvts.forEach((ev) => cellItems.push({ kind: 'custom', ev }));
              const MAX_VISIBLE = 3;
              const visible = cellItems.slice(0, MAX_VISIBLE);
              const overflow = cellItems.length - MAX_VISIBLE;
              const isExpanded = expandedDay === dateStr;

              const renderItem = (item, i) => {
                if (item.kind === 'shoot') {
                  const job = item.job;
                  const hold = getHoldStatus(job.status);
                  const st = getShootTime(job);
                  return (
                    <div
                      key={`s-${job.id}-${i}`}
                      onClick={(e) => { e.stopPropagation(); navigateToJob(navigate, job.id); }}
                      className={`text-2xs leading-tight px-1 py-0.5 rounded text-white truncate cursor-pointer hover:opacity-80 ${hold === 'soft' ? 'opacity-60 border border-dashed border-white/30' : ''}`}
                      style={{ backgroundColor: CAL_COLORS.shoot, fontSize: '10px' }}
                      title={`${job.title}${st ? ` @ ${st}` : ''}${hold ? ` [${hold.toUpperCase()}]` : ''}`}
                    >
                      {st && <span className="opacity-70 mr-0.5">{fmtTime(st)}</span>}{truncate(job.title, st ? 10 : 14)}
                    </div>
                  );
                }
                if (item.kind === 'delivery') {
                  return (
                    <div key={`d-${i}`} className="text-2xs px-1 py-0.5 rounded truncate" style={{ background: 'rgba(201,74,42,0.12)', color: CAL_COLORS.delivery, fontSize: '10px' }}>
                      📦 {item.del.title ? truncate(item.del.title.replace('📦 ', ''), 14) : 'Delivery'}
                    </div>
                  );
                }
                if (item.kind === 'legal') {
                  return (
                    <div key={`l-${i}`} className="text-2xs px-1 py-0.5 rounded truncate" style={{ background: 'rgba(123,94,167,0.12)', color: CAL_COLORS.legal, fontSize: '10px' }}>
                      📑 {truncate(item.leg.title.replace('📑 ', ''), 14)}
                    </div>
                  );
                }
                if (item.kind === 'custom') {
                  return (
                    <div key={`e-${item.ev.id}-${i}`} className="text-2xs px-1 py-0.5 rounded bg-purple-400/15 text-purple-400 truncate" style={{ fontSize: '10px' }}>
                      {item.ev.title}
                    </div>
                  );
                }
                return null;
              };

              return (
                <div
                  key={idx}
                  onClick={() => {
                    if (expandedDay) { setExpandedDay(null); return; }
                    if (!cell.current) {
                      setCurrentDate(new Date(cell.year, cell.month, cell.day));
                      setSelectedDay(null);
                      return;
                    }
                    if (isSelected) {
                      const drillDate = new Date(year, month, cell.day);
                      setCurrentDate(drillDate);
                      setViewMode('week');
                      setSelectedDay(null);
                    } else {
                      setSelectedDay(cell.day);
                    }
                  }}
                  className={`relative min-h-[100px] p-1 border-b border-r border-border cursor-pointer transition-colors ${
                    isToday
                      ? 'bg-accent/8 ring-1 ring-inset ring-accent'
                      : isSelected
                      ? 'bg-accent/5 ring-1 ring-inset ring-accent/50'
                      : !cell.current
                      ? 'bg-surface/50'
                      : isWeekend
                      ? 'bg-surface hover:bg-surface-2'
                      : 'hover:bg-surface-2'
                  }`}
                  style={idx % 7 === 0 ? { borderLeft: '1px solid var(--border)' } : undefined}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`text-2xs font-semibold leading-none ${
                      isToday ? 'bg-accent text-bg w-5 h-5 rounded-full flex items-center justify-center'
                      : !cell.current ? 'text-text-dim/40'
                      : 'text-text-primary'
                    }`}>
                      {cell.day}
                    </span>
                    {cell.current && forecast['Santa Barbara']?.[dateStr] && (() => {
                      const fw = forecast['Santa Barbara'][dateStr];
                      const wi = weatherInfo(fw.code);
                      return (
                        <span className="text-2xs flex items-center gap-0.5" title={`${wi.desc} · ${fw.high}°/${fw.low}° · ${fw.wind} mph`}>
                          <span style={{ fontSize: '9px' }}>{wi.icon}</span>
                          <span className="text-text-dim" style={{ fontSize: '9px' }}>{fw.high}°</span>
                        </span>
                      );
                    })()}
                  </div>
                  {cell.current && (
                    <div className="flex flex-col gap-0.5">
                      {visible.map(renderItem)}
                      {overflow > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setExpandedDay(isExpanded ? null : dateStr); }}
                          className="text-accent hover:text-accent-hover text-left px-1 font-semibold"
                          style={{ fontSize: '9px' }}
                        >
                          +{overflow} more
                        </button>
                      )}
                    </div>
                  )}
                  {!cell.current && cellItems.length > 0 && (
                    <div className="flex flex-col gap-0.5 opacity-40">
                      {visible.map(renderItem)}
                      {overflow > 0 && (
                        <div className="text-text-dim text-left px-1" style={{ fontSize: '9px' }}>
                          +{overflow} more
                        </div>
                      )}
                    </div>
                  )}
                  {/* Overflow dropdown */}
                  {isExpanded && overflow > 0 && (
                    <div
                      className="absolute left-0 top-full z-30 w-56 bg-surface border border-border rounded-lg shadow-xl p-2 space-y-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-text-primary">
                          {new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <button onClick={() => setExpandedDay(null)} className="text-text-dim hover:text-text-primary text-xs">✕</button>
                      </div>
                      {cellItems.map(renderItem)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Month agenda */}
      {viewMode === 'month' && (() => {
        const mStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const mEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
        return renderAgenda(mStart, mEnd, `${monthName} ${year} Agenda`);
      })()}

      {/* ── WEEK VIEW ──────────────────────────────────────────────── */}
      {viewMode === 'week' && (
        <div className="card">
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day) => {
              const dateStr = toDateStr(day);
              const dayJobs = jobsByDate[dateStr] || [];
              const dayDeliveries = deliveriesByDate[dateStr] || [];
              const dayLegals = legalsByDate[dateStr] || [];
              const dayCustom = eventsByDate[dateStr] || [];
              const isToday = isSameDay(day, today);
              const isSelected = selectedDay === dateStr;

              return (
                <div
                  key={dateStr}
                  onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                  className={`min-h-[200px] p-2 rounded border-2 cursor-pointer transition-colors ${
                    isToday
                      ? 'border-accent bg-accent/5'
                      : isSelected
                      ? 'border-accent bg-surface-2'
                      : 'border-border hover:border-border-light'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-semibold ${isToday ? 'text-accent' : 'text-text-primary'}`}>
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                    <span className={`text-sm font-heading ${isToday ? 'text-accent' : 'text-text-primary'}`}>
                      {day.getDate()}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {dayJobs.map((job) => {
                      const hold = getHoldStatus(job.status);
                      const crew = getCrewNames(job);
                      const st = getShootTime(job);
                      return (
                        <div
                          key={job.id}
                          onClick={(e) => { e.stopPropagation(); navigateToJob(navigate, job.id); }}
                          className={`text-2xs px-1.5 py-1 rounded text-white cursor-pointer hover:opacity-80 transition-opacity ${hold === 'soft' ? 'opacity-60 border border-dashed border-white/30' : ''}`}
                          style={{ backgroundColor: CAL_COLORS.shoot }}
                          title={`${job.title} — ${job.clientName || 'N/A'}${st ? ` @ ${st}` : ''}${hold ? ` [${hold.toUpperCase()}]` : ''}${crew.length ? `\nCrew: ${crew.join(', ')}` : ''}`}
                        >
                          <div className="font-semibold truncate">
                            {hold && <span className="text-white/70 mr-1">{hold === 'soft' ? '○' : '●'}</span>}
                            {st && <span className="opacity-70 font-normal mr-1">{fmtTime(st)}</span>}
                            {truncate(job.title, 16)}
                          </div>
                          <div className="truncate opacity-80">{job.clientName || ''}</div>
                          {crew.length > 0 && (
                            <div className="truncate opacity-60 text-white/70" style={{ fontSize: '9px' }}>
                              🎥 {crew.join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {dayDeliveries.map((del, i) => (
                      <div
                        key={`del-${i}`}
                        onClick={(e) => { e.stopPropagation(); navigateToJob(navigate, del.jobId); }}
                        className={`text-2xs px-1.5 py-1 rounded cursor-pointer hover:opacity-80 transition-opacity ${del.delivered ? 'line-through opacity-50' : ''}`}
                        style={{ background: 'rgba(201,74,42,0.15)', color: CAL_COLORS.delivery, borderLeft: `2px solid ${CAL_COLORS.delivery}` }}
                      >
                        <div className="truncate">{del.delivered ? '✅' : '📦'} {del.title}</div>
                      </div>
                    ))}
                    {dayLegals.map((leg, i) => (
                      <div
                        key={`leg-${i}`}
                        className="text-2xs px-1.5 py-1 rounded"
                        style={{ background: 'rgba(123,94,167,0.15)', color: CAL_COLORS.legal, borderLeft: `2px solid ${CAL_COLORS.legal}` }}
                      >
                        <div className="truncate">{leg.title}</div>
                      </div>
                    ))}
                    {dayCustom.map((ev, i) => (
                      <div
                        key={`cus-${i}`}
                        className="text-2xs px-1.5 py-1 rounded"
                        style={{ background: 'rgba(139,92,246,0.15)', color: CAL_COLORS.custom, borderLeft: `2px solid ${CAL_COLORS.custom}` }}
                      >
                        <div className="truncate">{ev.time ? `${fmtTime(ev.time)} ` : ''}{ev.title}</div>
                      </div>
                    ))}
                    {dayJobs.length === 0 && dayDeliveries.length === 0 && dayLegals.length === 0 && dayCustom.length === 0 && (
                      <div className="text-2xs text-text-dim italic">No events</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week agenda */}
      {viewMode === 'week' && (() => {
        const wStart = toDateStr(weekDays[0]);
        const wEnd = toDateStr(weekDays[6]);
        return renderAgenda(wStart, wEnd, `Week of ${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
      })()}

      {/* ── YEAR VIEW ───────────────────────────────────────── */}
      {viewMode === 'year' && (
        <div className="card">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 12 }, (_, mi) => {
              const isCurrent = today.getFullYear() === year && today.getMonth() === mi;
              const monthFirst = new Date(year, mi, 1);
              const mFirstDay = monthFirst.getDay();
              const mDays = new Date(year, mi + 1, 0).getDate();
              const mName = monthFirst.toLocaleDateString('en-US', { month: 'short' });

              return (
                <div
                  key={mi}
                  onClick={() => { setCurrentDate(new Date(year, mi, 1)); setViewMode('month'); }}
                  className={`p-2 rounded border-2 cursor-pointer transition-colors ${
                    isCurrent ? 'border-accent bg-accent/5' : 'border-border hover:border-border-light'
                  }`}
                >
                  <div className={`text-xs font-semibold mb-1.5 ${isCurrent ? 'text-accent' : 'text-text-primary'}`}>
                    {mName}
                  </div>
                  <div className="grid grid-cols-7 gap-px">
                    {/* Day headers */}
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                      <div key={i} className="text-center text-text-dim" style={{ fontSize: '7px' }}>{d}</div>
                    ))}
                    {/* Empty cells */}
                    {Array.from({ length: mFirstDay }, (_, i) => (
                      <div key={`e${i}`} />
                    ))}
                    {/* Day numbers */}
                    {Array.from({ length: mDays }, (_, i) => {
                      const dayNum = i + 1;
                      const ds = `${year}-${String(mi + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                      const hasJobs = !!jobsByDate[ds];
                      const hasDeliveries = !!deliveriesByDate[ds];
                      const hasLegals = !!legalsByDate[ds];
                      const hasAny = hasJobs || hasDeliveries || hasLegals;
                      const isToday2 = today.getFullYear() === year && today.getMonth() === mi && today.getDate() === dayNum;
                      return (
                        <div
                          key={dayNum}
                          className={`text-center rounded-sm ${isToday2 ? 'bg-accent text-bg font-bold' : hasJobs ? 'bg-accent/20 text-accent font-semibold' : hasDeliveries ? 'text-[#C94A2A] font-semibold' : hasLegals ? 'text-[#7B5EA7] font-semibold' : 'text-text-dim'}`}
                          style={{ fontSize: '8px', lineHeight: '14px' }}
                        >
                          {dayNum}
                        </div>
                      );
                    })}
                  </div>
                  {/* Month shoot count */}
                  {(() => {
                    const mPrefix = `${year}-${String(mi + 1).padStart(2, '0')}`;
                    const shootCount = Object.keys(jobsByDate).filter(k => k.startsWith(mPrefix)).reduce((s, k) => s + jobsByDate[k].length, 0);
                    const delCount = Object.keys(deliveriesByDate).filter(k => k.startsWith(mPrefix)).reduce((s, k) => s + deliveriesByDate[k].length, 0);
                    const mCount = shootCount + delCount;
                    return mCount > 0 ? (
                      <div className="text-2xs mt-1 text-center">
                        {shootCount > 0 && <span style={{ color: CAL_COLORS.shoot }}>{shootCount} 🎬</span>}
                        {shootCount > 0 && delCount > 0 && ' '}
                        {delCount > 0 && <span style={{ color: CAL_COLORS.delivery }}>{delCount} 📦</span>}
                      </div>
                    ) : null;
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Year agenda */}
      {viewMode === 'year' && (() => {
        const yStart = `${year}-01-01`;
        const yEnd = `${year}-12-31`;
        return renderAgenda(yStart, yEnd, `${year} Agenda`);
      })()}

      {/* ── Add Event Modal ────────────────────────────────────────── */}
      {showEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowEventModal(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-surface border border-border rounded-lg shadow-xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-heading text-text-primary">New Calendar Event</h3>
            <input
              type="text"
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              placeholder="Event title..."
              autoFocus
              className="w-full px-3 py-2 bg-bg border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent"
            />
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-2xs text-text-dim block mb-1">Date</label>
                <input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  className="w-full px-3 py-2 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="text-2xs text-text-dim block mb-1">Time</label>
                <input
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                  className="px-3 py-2 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent"
                />
              </div>
            </div>
            <input
              type="text"
              value={eventForm.notes}
              onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
              placeholder="Notes (optional)"
              className="w-full px-3 py-2 bg-bg border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent"
            />
            <div>
              <label className="text-2xs text-text-dim block mb-1">Link to Job (optional)</label>
              <select
                value={eventForm.jobId}
                onChange={(e) => {
                  const jobId = e.target.value;
                  setEventForm({ ...eventForm, jobId });
                  if (jobId && !eventForm.title) {
                    const job = allJobs.find(j => j.id === jobId);
                    if (job) setEventForm(prev => ({ ...prev, jobId, title: job.title || job.clientName || '' }));
                  }
                }}
                className="w-full px-3 py-2 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent"
              >
                <option value="">— No job —</option>
                {allJobs.filter(j => j.status !== 'archived').slice(0, 50).map(j => (
                  <option key={j.id} value={j.id}>{j.title || j.clientName || j.id}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setShowEventModal(false)}
                className="px-4 py-2 bg-surface-2 hover:bg-surface-3 text-text-primary text-xs font-semibold rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!eventForm.title.trim() || !eventForm.date) return;
                  createCalEvent.mutate({
                    id: uid(),
                    title: eventForm.title.trim(),
                    date: eventForm.date,
                    time: eventForm.time || null,
                    notes: eventForm.notes.trim() || null,
                    color: eventForm.jobId ? '#10B981' : '#8B5CF6',
                    job_id: eventForm.jobId || null,
                    event_type: eventForm.jobId ? 'shoot' : 'custom',
                  });
                  setShowEventModal(false);
                  setEventForm({ title: '', date: '', time: '', notes: '', jobId: '' });
                }}
                disabled={!eventForm.title.trim() || !eventForm.date}
                className="px-4 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New Job Modal ───────────────────────────────────────────── */}
      {showNewJobModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowNewJobModal(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-surface border border-border rounded-lg shadow-xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-heading text-text-primary">New Job</h3>
            <div>
              <label className="text-2xs text-text-dim block mb-1">Shoot Date</label>
              <input
                type="date"
                value={newJobDate}
                onChange={(e) => setNewJobDate(e.target.value)}
                autoFocus
                className="w-full px-3 py-2 bg-bg border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setShowNewJobModal(false)}
                className="px-4 py-2 bg-surface-2 hover:bg-surface-3 text-text-primary text-xs font-semibold rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!newJobDate) return;
                  setShowNewJobModal(false);
                  handleCreateJob(newJobDate);
                }}
                disabled={!newJobDate}
                className="px-4 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
